import { BoxProps } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCapitalActivityTableProps } from "features/capital-activity/table/CapitalActivityTableProps";
import {
  getCapitalActivityInvestorOverviewRetrieveQueryKey,
  useCapitalActivityEventRetrieveSuspense,
} from "gen/endpoints/capital-activity/capital-activity";
import {
  useFundFamilySubEventListSuspense,
  useWaterfallRunListSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import {
  DataTableOnButtonClickFn,
  RenderCellValueProps,
} from "shared/components/table/data/DataTableTypes";
import { useToast } from "shared/components/toast/useToast";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";

export interface CapitalEventTableProps {
  fundFamilyId: string;
  fundFamilyEventId: string;
  isFullWidth?: boolean;
  isCellLoading?: (props: RenderCellValueProps) => boolean;
  onButtonClick?: DataTableOnButtonClickFn;
  boxProps?: BoxProps;
}

export const CapitalEventTable: React.FC<CapitalEventTableProps> = ({
  fundFamilyId,
  fundFamilyEventId,
  isFullWidth,
  isCellLoading,
  onButtonClick,
  boxProps,
}) => {
  const { t } = useTranslation(["capitalActivity"]);
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data: eventData, refetch: refetchActivityTable } =
    useCapitalActivityEventRetrieveSuspense(fundFamilyId, fundFamilyEventId);

  const { data: subevents } = useFundFamilySubEventListSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const renderEqualizationToasts = (isError: boolean) => {
    if (isError) {
      showToast({
        message: t("capitalActivity:celeryToasts.equalizations.error"),
        variant: "error",
      });
    } else {
      showToast({
        message: t("capitalActivity:celeryToasts.equalizations.success"),
        variant: "success",
      });
    }
  };

  const equalizationCeleryWorkflowIds = subevents.flatMap((subevent) =>
    subevent.celery_workflow_id ? [subevent.celery_workflow_id] : [],
  );

  const refetchData = async () => {
    await Promise.all([
      refetchActivityTable(),
      queryClient.invalidateQueries({
        queryKey: getCapitalActivityInvestorOverviewRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
      }),
    ]);
  };

  useCeleryWorkflowPoll(
    equalizationCeleryWorkflowIds,
    renderEqualizationToasts,
    refetchData,
    refetchData,
  );

  const renderWaterfallToasts = (isError: boolean) => {
    if (isError) {
      showToast({
        message: t("capitalActivity:celeryToasts.waterfalls.error"),
        variant: "error",
      });
    } else {
      showToast({
        message: t("capitalActivity:celeryToasts.waterfalls.success"),
        variant: "success",
      });
    }
  };
  const { data: waterfallRuns } = useWaterfallRunListSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const waterfallCeleryWorkflowIds = waterfallRuns.flatMap((waterfallRun) =>
    waterfallRun.celery_workflow_id ? [waterfallRun.celery_workflow_id] : [],
  );

  useCeleryWorkflowPoll(
    waterfallCeleryWorkflowIds,
    renderWaterfallToasts,
    refetchData,
    refetchData,
  );

  const tableProps = useCapitalActivityTableProps({
    data: eventData.capital_activity_data,
    fundFamilyId,
    fundFamilyEventId,
    isCellLoading,
  });

  return (
    <TableBox isFullWidth={isFullWidth} boxProps={boxProps}>
      <TableBoxHeader>
        <TableBoxTitle>{t("capitalActivity:events")}</TableBoxTitle>
        <DataTableBoxActions showSearch={false} showExport={false} />
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={eventData.capital_activity_data}
          size="comfortable"
          lockColumns={true}
          onButtonClick={onButtonClick}
          sx={{
            ".ag-header-cell, .ag-cell": {
              borderRightWidth: "1px",
              borderRightColor: "gray.200",
            },
          }}
          {...tableProps}
        />
      </TableBoxBody>
    </TableBox>
  );
};
